import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import LogoutConfirmationModal from "../modals/LogoutConfirmationModal";
import {
  LayoutDashboard,
  CreditCard,
  UserPlus,
  BookOpen,
  Calendar,
  Trophy,
  Bell,
  Clock,
  LogOut,
  Users,
  Building2,
  Settings,
  BarChart3,
  UserCheck,
  DollarSign,
  FileText,
  Shield,
  Activity,
  X,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Video,
  ClipboardList,
  Target,
  ChevronLeft,
  Globe,
  MessageSquare,
} from "lucide-react";

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const location = useLocation();
  const { user, handleLogout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [elearningExpanded, setElearningExpanded] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Check if content is scrollable and handle scroll
  React.useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const checkScroll = () => {
      const isScrollable = sidebar.scrollHeight > sidebar.clientHeight;
      const isAtBottom =
        sidebar.scrollHeight - sidebar.scrollTop <= sidebar.clientHeight + 10;
      setShowScrollIndicator(isScrollable && !isAtBottom);
    };

    checkScroll();
    sidebar.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      sidebar.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [elearningExpanded, isCollapsed]);

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    handleLogout();
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };

  // Get E-learning menu items based on user role
  const getElearningItems = () => {
    if (!user || !user.roles) return [];

    const roleBasedItems = [];

    // Student E-learning items
    if (user.roles.includes("student")) {
      roleBasedItems.push(
        {
          name: "E-Learning Dashboard",
          path: "/student/elearning",
          icon: LayoutDashboard,
        },
        {
          name: "My E-Courses",
          path: "/student/elearning/my-courses",
          icon: Video,
        },
        {
          name: "E-Course Catalog",
          path: "/student/elearning/catalog",
          icon: GraduationCap,
        },
        {
          name: "Online Quizzes",
          path: "/student/elearning/quizzes",
          icon: Target,
        },

        {
          name: "Live Sessions",
          path: "/student/elearning/live-sessions",
          icon: Video,
        },
      );
    }

    // Teacher E-learning items
    if (user.roles.includes("teacher")) {
      roleBasedItems.push(
        {
          name: "E-Learning Hub",
          path: "/elearning/dashboard",
          icon: GraduationCap,
        },
        { name: "Regular Courses", path: "/teacher/courses", icon: BookOpen },
        { name: "My E-Courses", path: "/elearning/courses", icon: Video },
        {
          name: "Quiz Management",
          path: "/elearning/quizzes",
          icon: ClipboardList,
        },

        {
          name: "Live Sessions",
          path: "/elearning/teacher/live-sessions",
          icon: Video,
        },
      );
    }

    // Branch Admin E-learning items
    if (user.roles.includes("branchadmin") || user.roles.includes("admin")) {
      roleBasedItems.push(
        {
          name: "E-course Management",
          path: "/admin/elearning",
          icon: Settings,
        },
        {
          name: "Analytics Dashboard",
          path: "/admin/elearning/analytics",
          icon: BarChart3,
        },
        {
          name: "E-course Students",
          path: "/admin/elearning/students",
          icon: Users,
        },
        {
          name: "Manual Enrollment",
          path: "/admin/elearning/enroll-students",
          icon: Users,
        },
      );
    }

    return roleBasedItems;
  };

  // Get menu items based on user role
  const getMenuItems = () => {
    if (!user || !user.roles) return [];

    // SuperAdmin navigation
    if (user.roles.includes("superadmin")) {
      return [
        { name: "Dashboard", path: "/superadmin", icon: LayoutDashboard },
        { name: "Landing Page Management", path: "/landing-page", icon: Globe },
        { name: "Branch Management", path: "/branches", icon: Building2 },
        { name: "User Management", path: "/users", icon: Users },
        {
          name: "Expense Management",
          path: "/expense-management",
          icon: CreditCard,
        },
        { name: "WhatsApp", path: "/whatsapp", icon: MessageSquare },
        { name: "System Analytics", path: "/system-analytics", icon: Activity },
        {
          name: "Financial Reports",
          path: "/financial-reports",
          icon: DollarSign,
        },
        // { name: "Global Settings", path: "/settings", icon: Settings },
        // { name: "Security & Roles", path: "/security", icon: Shield },
        { name: "Audit Logs", path: "/audit-logs", icon: FileText },
      ];
    }

    // Admin and Branch Admin navigation (both use admin routes)
    if (user.roles.includes("admin") || user.roles.includes("branchadmin")) {
      return [
        { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
        { name: "Students", path: "/admin/students", icon: UserPlus },
        { name: "Teachers", path: "/admin/teachers", icon: UserCheck },
        { name: "Classes", path: "/admin/classes", icon: Calendar },
        { name: "Courses", path: "/admin/courses", icon: BookOpen },
        { name: "Departments", path: "/admin/departments", icon: Building2 },
        { name: "Fees", path: "/fees", icon: CreditCard },
        { name: "Expenses", path: "/admin/expenses", icon: DollarSign },

        { name: "Attendance", path: "/admin/attendance", icon: Clock },
        { name: "Announcements", path: "/admin/announcements", icon: Bell },
        { name: "Reports", path: "/reports", icon: BarChart3 },
      ];
    }

    // // Admin navigation
    // if (user.roles.includes("admin")) {
    //   return [
    //     { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
    //     { name: "Students", path: "/admin/students", icon: UserPlus },
    //     { name: "Teachers", path: "/admin/teachers", icon: UserCheck },
    //     { name: "Classes", path: "/admin/classes", icon: Calendar },
    //     { name: "Courses", path: "/courses", icon: BookOpen },
    //     { name: "Fees", path: "/fees", icon: CreditCard },
    //     { name: "Expenses", path: "/admin/expenses", icon: DollarSign },
    //     { name: "Attendance", path: "/attendance", icon: Clock },
    //     { name: "Announcements", path: "/admin/announcements", icon: Bell },
    //     { name: "Reports", path: "/reports", icon: BarChart3 },
    //     { name: "Settings", path: "/settings", icon: Settings },
    //   ];
    // }

    // Secretary navigation
    if (user.roles.includes("secretary")) {
      return [
        { name: "Dashboard", path: "/secretary", icon: LayoutDashboard },
        {
          name: "Student Registration",
          path: "/secretary/students",
          icon: UserPlus,
        },
        {
          name: "Fee Management",
          path: "/secretary/payments",
          icon: CreditCard,
        },
        {
          name: "Expenses",
          path: "/secretary/expenses",
          icon: DollarSign,
        },
        { name: "Schedule", path: "/secretary/schedule", icon: Calendar },
        { name: "Announcements", path: "/secretary/announcements", icon: Bell },
        {
          name: "Attendance Records",
          path: "/secretary/attendance",
          icon: Clock,
        },
        { name: "Reports", path: "/secretary/reports", icon: BarChart3 },
      ];
    }

    // Teacher navigation (CMS only)
    if (user.roles.includes("teacher")) {
      return [
        {
          name: "Dashboard",
          path: "/teacher/dashboard",
          icon: LayoutDashboard,
        },
        { name: "My Classes", path: "/teacher/classes", icon: Users },
        { name: "Schedule", path: "/teacher/schedule", icon: Clock },
        { name: "Attendance", path: "/teacher/attendance", icon: UserCheck },
        { name: "Students", path: "/teacher/students", icon: UserPlus },
        { name: "Exams &Grades", path: "/teacher/grades", icon: Trophy },
        { name: "Announcements", path: "teacher/announcements", icon: Bell },
        { name: "Profile", path: "/teacher/profile", icon: Settings },
      ];
    }

    // Student navigation (CMS only)
    if (user.roles.includes("student")) {
      // Check if this is an e-course student using user context
      const isECourseStudent = user.studentType === "ecourse";

      if (isECourseStudent) {
        // E-course student navigation - only profile
        return [{ name: "Profile", path: "/student/profile", icon: UserPlus }];
      } else {
        // Regular student navigation
        return [
          {
            name: "Dashboard",
            path: "/student/dashboard",
            icon: LayoutDashboard,
          },
          { name: "My Courses", path: "/student/courses", icon: BookOpen },
          { name: "My Grades", path: "/student/grades", icon: Trophy },
          { name: "Attendance", path: "/student/attendance", icon: Clock },
          { name: "Fee Payments", path: "/student/fees", icon: CreditCard },
          { name: "Schedule", path: "/student/schedule", icon: Calendar },
          { name: "Notices", path: "/student/notices", icon: Bell },
          { name: "Profile", path: "/student/profile", icon: UserPlus },
        ];
      }
    }

    // Default fallback navigation (should not be reached normally)
    return [{ name: "Dashboard", path: "/dashboard", icon: LayoutDashboard }];
  };

  const menuItems = getMenuItems();
  const isActive = (path: string) => location.pathname === path;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div
      ref={sidebarRef}
      className={`
      fixed lg:relative top-0 left-0 h-screen bg-gradient-to-b from-white via-slate-50 to-blue-50 border-r border-slate-200/60 text-slate-700 flex flex-col shadow-2xl lg:shadow-xl z-20 overflow-y-auto overflow-x-hidden backdrop-blur-xl
      transform transition-all duration-300 ease-in-out
      scrollbar-hide
      ${isCollapsed ? "lg:w-20" : "lg:w-64"}
      ${isOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0"}
    `}
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {/* Logo/Brand */}
      <div className="p-4 lg:p-6 border-b border-slate-200/60 bg-gradient-to-r from-white to-slate-50/80 backdrop-blur-xl">
        <div className="flex items-center justify-between mb-2">
          {!isCollapsed && (
            <h2 className="text-lg lg:text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate">
              {user?.roles?.includes("superadmin")
                ? "Super Admin Portal"
                : user?.roles?.includes("admin") ||
                    user?.roles?.includes("branchadmin")
                  ? "Admin Portal"
                  : user?.roles?.includes("secretary")
                    ? "Secretary Portal"
                    : user?.roles?.includes("teacher")
                      ? "Teacher Portal"
                      : "Student Portal"}
            </h2>
          )}
          {/* Collapse/Expand button for desktop */}
          <button
            onClick={toggleCollapse}
            className="hidden lg:block p-1.5 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 hover:from-blue-100 hover:to-indigo-100 border border-slate-200/60 transition-all duration-300 hover:shadow-md"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-slate-700" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-slate-700" />
            )}
          </button>
          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 hover:from-blue-100 hover:to-indigo-100 border border-slate-200/60 transition-all duration-300 hover:shadow-md"
          >
            <X className="w-4 h-4 text-slate-700" />
          </button>
        </div>
        {!isCollapsed && (user as any)?.branch?.name && (
          <div className="flex items-center gap-2 mt-2 px-2 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
            <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <p className="text-xs font-semibold text-slate-700 truncate">
              {(user as any).branch.name}
            </p>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-2 lg:py-4">
        {/* CMS Navigation */}
        <ul className="space-y-1 lg:space-y-2 px-2 lg:px-4">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={handleLinkClick}
                  className={`flex items-center ${
                    isCollapsed ? "justify-center" : "space-x-3"
                  } px-3 lg:px-4 py-2 lg:py-3 rounded-xl transition-all duration-300 group relative ${
                    isActive(item.path)
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-[#75c92d] shadow-lg shadow-blue-500/30"
                      : "text-[#75c92d] hover:bg-gradient-to-br hover:from-slate-50 hover:to-blue-50 hover:text-[#75c92d] hover:shadow-md"
                  }`}
                  title={isCollapsed ? item.name : ""}
                >
                  <Icon
                    className={`w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0 ${
                      isActive(item.path)
                        ? "text-[#75c92d]"
                        : "text-[#75c92d] group-hover:text-[#75c92d]"
                    }`}
                  />
                  {!isCollapsed && (
                    <span className="font-semibold text-sm lg:text-base truncate">
                      {item.name}
                    </span>
                  )}
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-3 py-1.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200 z-50 shadow-xl">
                      {item.name}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* E-Learning Section */}
        {(user?.roles?.includes("student") ||
          user?.roles?.includes("teacher") ||
          user?.roles?.includes("branchadmin") ||
          user?.roles?.includes("admin")) && (
          <div className="px-2 lg:px-4 mt-4">
            {/* E-learning Header */}
            <button
              onClick={() => setElearningExpanded(!elearningExpanded)}
              className={`flex items-center ${
                isCollapsed ? "justify-center" : "justify-between"
              } w-full px-3 lg:px-4 py-2 lg:py-3 text-slate-700 hover:bg-gradient-to-br hover:from-slate-50 hover:to-blue-50 rounded-xl transition-all duration-300 group relative hover:shadow-md`}
              title={isCollapsed ? "E-Learning" : ""}
            >
              {!isCollapsed && (
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center shadow-md">
                    <GraduationCap className="w-4 h-4 lg:w-5 lg:h-5 text-white flex-shrink-0" />
                  </div>
                  <span className="font-bold text-sm lg:text-base bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    E-Learning
                  </span>
                </div>
              )}
              {isCollapsed && (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center shadow-md">
                  <GraduationCap className="w-4 h-4 lg:w-5 lg:h-5 text-white flex-shrink-0" />
                </div>
              )}
              {!isCollapsed &&
                (elearningExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-500 transition-transform duration-300" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-500 transition-transform duration-300" />
                ))}
              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-1.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200 z-50 shadow-xl">
                  E-Learning
                </div>
              )}
            </button>

            {/* E-learning Items */}
            {elearningExpanded && !isCollapsed && (
              <ul className="mt-2 space-y-1 lg:space-y-2 ml-4">
                {getElearningItems().map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={handleLinkClick}
                        className={`flex items-center space-x-3 px-3 lg:px-4 py-2 lg:py-3 rounded-xl transition-all duration-300 group ${
                          isActive(item.path)
                            ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-[#75c92d] shadow-lg shadow-blue-500/30"
                            : "text-[#75c92d] hover:bg-gradient-to-br hover:from-slate-50 hover:to-blue-50 hover:text-[#75c92d] hover:shadow-md"
                        }`}
                      >
                        <Icon
                          className={`w-4 h-4 lg:w-5 lg:h-5 flex-shrink-0 ${
                            isActive(item.path)
                              ? "text-[#75c92d]"
                              : "text-[#75c92d] group-hover:text-[#75c92d]"
                          }`}
                        />
                        <span className="font-semibold text-sm lg:text-base truncate">
                          {item.name}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </nav>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-16 left-0 right-0 h-12 bg-gradient-to-t from-white/90 to-transparent pointer-events-none flex items-end justify-center pb-2">
          <div className="animate-bounce">
            <ChevronDown className="w-5 h-5 text-blue-500" />
          </div>
        </div>
      )}

      {/* Logout Button */}
      <div className="p-2 lg:p-4 border-t border-slate-200/60 bg-gradient-to-r from-white to-slate-50/80 backdrop-blur-xl">
        <button
          onClick={handleLogoutClick}
          className={`flex items-center ${
            isCollapsed ? "justify-center" : "space-x-3"
          } px-3 lg:px-4 py-2 lg:py-3 rounded-xl text-slate-600 hover:bg-gradient-to-br hover:from-red-50 hover:to-pink-50 hover:text-red-600 transition-all duration-300 w-full group relative hover:shadow-md border border-transparent hover:border-red-200/60`}
          title={isCollapsed ? "Logout" : ""}
        >
          <LogOut className="w-4 h-4 lg:w-5 lg:h-5 text-slate-400 group-hover:text-red-500 flex-shrink-0" />
          {!isCollapsed && (
            <span className="font-semibold text-sm lg:text-base">Logout</span>
          )}
          {/* Tooltip for collapsed state */}
          {isCollapsed && (
            <div className="absolute left-full ml-2 px-3 py-1.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200 z-50 shadow-xl">
              Logout
            </div>
          )}
        </button>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        userFirstName={user?.firstName}
      />
    </div>
  );
};

export default Sidebar;
